import { useEffect, useRef } from 'react'
import { X, Folder, FolderOpen } from 'lucide-react'
import type { Project } from './CreateProjectModal'

interface MoveToProjectModalProps {
  convTitle: string
  projects: Project[]
  currentProjectId: string | null | undefined
  onClose: () => void
  onMove: (projectId: string | null) => void
}

export function MoveToProjectModal({
  convTitle,
  projects,
  currentProjectId,
  onClose,
  onMove,
}: MoveToProjectModalProps) {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose()
    }
    const keyHandler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', handler)
    document.addEventListener('keydown', keyHandler)
    return () => {
      document.removeEventListener('mousedown', handler)
      document.removeEventListener('keydown', keyHandler)
    }
  }, [onClose])

  const displayTitle = convTitle.length > 36 ? convTitle.slice(0, 36) + '…' : convTitle

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 99999,
        background: 'rgba(0,0,0,0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={onClose}
    >
      <div
        ref={ref}
        style={{
          background: '#1E1E20',
          border: '1px solid #2E2E30',
          borderRadius: 14,
          padding: '20px',
          width: 340,
          maxHeight: '80vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <div>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: '#E5E5E5' }}>
              Flytt til prosjekt
            </h3>
            <p style={{ margin: '3px 0 0', fontSize: 12, color: '#6A6A6A' }}>
              "{displayTitle}"
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'transparent', border: 'none', cursor: 'pointer',
              color: '#6A6A6A', display: 'flex', alignItems: 'center', justifyContent: 'center',
              width: 26, height: 26, borderRadius: 6, flexShrink: 0,
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#2A2A2C')}
            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
          >
            <X size={15} />
          </button>
        </div>

        {/* Project list */}
        <div style={{ overflowY: 'auto', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {/* Remove from project option */}
          {currentProjectId && (
            <button
              onClick={() => { onMove(null); onClose() }}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '9px 12px', borderRadius: 8, border: '1px solid #2E2E30',
                background: 'transparent', color: '#9A9A9A', fontSize: 13,
                cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left', width: '100%',
                transition: 'all 0.12s',
              }}
              onMouseEnter={e => {
                e.currentTarget.style.background = '#2A2A2C'
                e.currentTarget.style.color = '#E5E5E5'
              }}
              onMouseLeave={e => {
                e.currentTarget.style.background = 'transparent'
                e.currentTarget.style.color = '#9A9A9A'
              }}
            >
              <X size={14} style={{ flexShrink: 0 }} />
              <span>Fjern fra prosjekt</span>
            </button>
          )}

          {projects.length === 0 ? (
            <p style={{ fontSize: 13, color: '#5A5A5A', padding: '8px 4px', textAlign: 'center' }}>
              Ingen prosjekter ennå
            </p>
          ) : (
            projects.map(project => {
              const isActive = project.id === currentProjectId
              return (
                <button
                  key={project.id}
                  onClick={() => { if (!isActive) { onMove(project.id); onClose() } }}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '9px 12px', borderRadius: 8,
                    border: isActive ? '1px solid rgba(26,147,254,0.4)' : '1px solid #2E2E30',
                    background: isActive ? 'rgba(26,147,254,0.08)' : 'transparent',
                    color: isActive ? '#1A93FE' : '#C0C0C0', fontSize: 13,
                    cursor: isActive ? 'default' : 'pointer', fontFamily: 'inherit',
                    textAlign: 'left', width: '100%', transition: 'all 0.12s',
                  }}
                  onMouseEnter={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = '#2A2A2C'
                      e.currentTarget.style.color = '#E5E5E5'
                    }
                  }}
                  onMouseLeave={e => {
                    if (!isActive) {
                      e.currentTarget.style.background = 'transparent'
                      e.currentTarget.style.color = '#C0C0C0'
                    }
                  }}
                >
                  {isActive
                    ? <FolderOpen size={14} style={{ flexShrink: 0 }} />
                    : <Folder size={14} style={{ flexShrink: 0 }} />
                  }
                  <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {project.name}
                  </span>
                  {isActive && (
                    <span style={{ fontSize: 10, color: '#1A93FE', flexShrink: 0 }}>Nåværende</span>
                  )}
                </button>
              )
            })
          )}
        </div>
      </div>
    </div>
  )
}
